import { memo, useMemo } from "react";
import { CustomFieldType } from "@Clients/CustomFieldsClient";
import { Box, Typography } from "@mui/material";
import { Dropdown } from "@components/Dropdown/Dropdown";
import { FormInput } from "@components/FormInput/FormInput";

type PreviewFieldProps = {
  previewField: {
    label: string;
    type: CustomFieldType;
    placeholder?: string;
    required: boolean;
    help_text?: string;
    field_options?: Array<{ label: string; value: string }>;
  };
};

export const PreviewField = memo(({ previewField }: PreviewFieldProps) => {
  const selectOptions = useMemo(
    () =>
      (previewField.field_options || []).map((option) => ({
        value: option.value,
        label: option.label,
      })),
    [previewField.field_options]
  );

  const renderPreviewField = () => {
    if (previewField.type === "textarea") {
      return (
        <Box sx={{ mb: 2, width: "100%" }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {previewField.label}
            {previewField.required && (
              <Typography component="span" color="error">
                {" *"}
              </Typography>
            )}
          </Typography>
          <textarea
            placeholder={previewField.placeholder}
            rows={4}
            style={{
              width: "100%",
              padding: "8px 12px",
              borderRadius: "6px",
              border: "1px solid #e0e0e0",
              fontSize: "14px",
              fontFamily: "inherit",
              resize: "vertical",
              minHeight: "80px",
            }}
          />
          {previewField.help_text && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {previewField.help_text}
            </Typography>
          )}
        </Box>
      );
    }

    if (previewField.type === "boolean") {
      return (
        <Box sx={{ mb: 2, width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <input type="checkbox" />
            <Typography variant="body2">
              {previewField.label}
              {previewField.required && (
                <Typography component="span" color="error">
                  {" *"}
                </Typography>
              )}
            </Typography>
          </Box>
          {previewField.help_text && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {previewField.help_text}
            </Typography>
          )}
        </Box>
      );
    }

    if (previewField.type === "select") {
      return (
        <Box sx={{ mb: 2, width: "100%" }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {previewField.label}
            {previewField.required && (
              <Typography component="span" color="error">
                {" *"}
              </Typography>
            )}
          </Typography>
          <Dropdown
            options={selectOptions}
            value={selectOptions[0]?.value}
            placeholder={
              selectOptions.length ? "Select an option" : "No options yet"
            }
            disabled={selectOptions.length === 0}
            fullWidth
            size="small"
          />
          {previewField.help_text && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {previewField.help_text}
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 2, width: "100%" }}>
        <FormInput
          label={`${previewField.label}${previewField.required ? " *" : ""}`}
          placeholder={previewField.placeholder}
          type={previewField.type === "date" ? "date" : previewField.type}
          style={{ width: "100%" }}
        />
        {previewField.help_text && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {previewField.help_text}
          </Typography>
        )}
      </Box>
    );
  };
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 2,
        flex: 1,
        backgroundColor: "background.paper",
        display: "flex",
        flexDirection: "column",


      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Preview
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          alignItems: "stretch",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {renderPreviewField()}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Preview updates as you configure the field.
      </Typography>
    </Box>
  );
});
